import { StateCreator } from "zustand";
import { GlobalState } from "../globalStore";
import appSettings from "@/config/appSettings";
import type {
  SortOrder,
  SituationCategory,
  SupportedLanguage,
} from "@/core/fastapiClient/sdk";

// Type for the checked situation entry
export type CheckedSituation = {
  situationID: string;
  category: SituationCategory;
};

export interface UserChoiceProfileSelectorState {
  //////* Profile selector
  // select situations
  checkedSituations: CheckedSituation[];
  addCheckedSituation: (situationID: string, category: string) => void;
  removeCheckedSituation: (situationID: string, category: string) => void;
  resetCheckedSituations: () => void;
  // reodered situations
  // select thoughts
  checkedThoughts: string[]; // Array of thought IDs
  addCheckedThought: (thoughtID: string) => void; // Add specifically
  removeCheckedThought: (thoughtID: string) => void; // Remove specifically
  resetCheckedThoughts: () => void;
  // edit mindstrings
  editedMindstrings: Record<string, string>;
  setEditedMindstrings: (thoughtID: string, mindstring: string) => void;
  resetEditedMindstrings: () => void;
  // active situation category (where the user is currently)
  activeSituationCategory: SituationCategory;
  setActiveSituationCategory: (category: SituationCategory) => void;
  // thought sort order
  sortOrder: SortOrder;
  setSortOrder: (order: SortOrder) => void;
  // seach for language
  searchForLanguage: SupportedLanguage | null;
  setSearchForLanguage: (language: SupportedLanguage | null) => void;
  resetSearchForLanguage: () => void;
}

export const createUserChoicesProfileSelectorSlice: StateCreator<
  GlobalState,
  [],
  [],
  UserChoiceProfileSelectorState
> = (set) => ({
  //////* Profile selector
  // select situations
  checkedSituations: [],
  addCheckedSituation: (situationID, category) =>
    set((state) => ({
      checkedSituations: [
        ...state.checkedSituations,
        { situationID, category: category as SituationCategory },
      ],
    })),

  removeCheckedSituation: (situationID, category) =>
    set((state) => ({
      checkedSituations: state.checkedSituations.filter(
        (item) =>
          !(item.situationID === situationID && item.category === category)
      ),
    })),
  resetCheckedSituations: () => set({ checkedSituations: [] }),
  // select thoughts
  checkedThoughts: [],
  addCheckedThought: (thoughtID) =>
    set((state) => ({
      checkedThoughts: [...state.checkedThoughts, thoughtID],
    })),
  removeCheckedThought: (thoughtID) =>
    set((state) => ({
      checkedThoughts: state.checkedThoughts.filter((id) => id !== thoughtID),
    })),
  resetCheckedThoughts: () => set({ checkedThoughts: [] }),
  // edit mindstrings
  editedMindstrings: {},
  setEditedMindstrings: (thoughtID, mindstring) =>
    set((state) => ({
      editedMindstrings: {
        ...state.editedMindstrings,
        [thoughtID]: mindstring,
      },
    })),
  resetEditedMindstrings: () => set({ editedMindstrings: {} }),
  // active situation category (where the user is currently)
  activeSituationCategory: appSettings.default.activeSituationCategory,
  setActiveSituationCategory: (category) =>
    set({ activeSituationCategory: category }),
  // thought sort order
  sortOrder: appSettings.default.sortOrder,
  setSortOrder: (order) => set({ sortOrder: order }),
  // seach for language
  searchForLanguage: null,
  setSearchForLanguage: (language) => set({ searchForLanguage: language }),
  resetSearchForLanguage: () => set({ searchForLanguage: null }),
});
